import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface WorkflowTemplate {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config: Record<string, any>;
  actions: Array<Record<string, any>>;
  conditions: Record<string, any>;
  is_active: boolean;
  usage_count: number;
  created_at: string;
  updated_at: string;
}

export interface WorkflowExecution {
  id: string;
  workflow_template_id: string;
  user_id: string;
  trigger_data: Record<string, any>;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  steps_completed: number;
  total_steps: number;
  error_message?: string;
  started_at: string;
  completed_at?: string;
}

export interface CreateWorkflowData {
  name: string;
  description?: string;
  trigger_type: string;
  trigger_config: Record<string, any>;
  actions: Array<Record<string, any>>;
  conditions?: Record<string, any>;
}

export function useWorkflowAutomation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Fetch workflow templates
  const workflowTemplatesQuery = useQuery({
    queryKey: ['workflow-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as WorkflowTemplate[];
    },
  });

  // Fetch workflow executions
  const workflowExecutionsQuery = useQuery({
    queryKey: ['workflow-executions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workflow_executions')
        .select(`
          *,
          workflow_templates:workflow_template_id (name, description)
        `)
        .order('started_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data as (WorkflowExecution & { workflow_templates: { name: string; description: string } })[];
    },
  });

  // Create workflow template
  const createWorkflowMutation = useMutation({
    mutationFn: async (workflowData: CreateWorkflowData) => {
      const { data, error } = await supabase
        .from('workflow_templates')
        .insert([{
          ...workflowData,
          user_id: (await supabase.auth.getUser()).data.user?.id
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
      toast({
        title: "Workflow created",
        description: "Your workflow template has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to create workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update workflow template
  const updateWorkflowMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<CreateWorkflowData> }) => {
      const { data, error } = await supabase
        .from('workflow_templates')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
      toast({
        title: "Workflow updated",
        description: "Your workflow template has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete workflow template
  const deleteWorkflowMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('workflow_templates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
      queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
      toast({
        title: "Workflow deleted",
        description: "The workflow template has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to delete workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Toggle workflow status
  const toggleWorkflowStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('workflow_templates')
        .update({ is_active: isActive })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-templates'] });
      toast({
        title: "Workflow status updated",
        description: "The workflow status has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to update workflow status",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Execute workflow manually
  const executeWorkflowMutation = useMutation({
    mutationFn: async ({ templateId, triggerData }: { templateId: string; triggerData?: Record<string, any> }) => {
      const { data, error } = await supabase
        .from('workflow_executions')
        .insert([{
          workflow_template_id: templateId,
          user_id: (await supabase.auth.getUser()).data.user?.id,
          trigger_data: triggerData || {},
          total_steps: 1 // Will be updated by the workflow engine
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workflow-executions'] });
      toast({
        title: "Workflow started",
        description: "The workflow execution has been started.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to execute workflow",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Get workflow statistics
  const getWorkflowStats = () => {
    const templates = workflowTemplatesQuery.data || [];
    const executions = workflowExecutionsQuery.data || [];
    
    return {
      totalTemplates: templates.length,
      activeTemplates: templates.filter(t => t.is_active).length,
      totalExecutions: executions.length,
      successfulExecutions: executions.filter(e => e.status === 'completed').length,
      failedExecutions: executions.filter(e => e.status === 'failed').length,
      runningExecutions: executions.filter(e => e.status === 'running').length,
      avgSuccessRate: executions.length > 0 
        ? (executions.filter(e => e.status === 'completed').length / executions.length) * 100 
        : 0,
    };
  };

  return {
    workflowTemplates: workflowTemplatesQuery.data || [],
    workflowExecutions: workflowExecutionsQuery.data || [],
    workflowStats: getWorkflowStats(),
    isLoading: workflowTemplatesQuery.isLoading || workflowExecutionsQuery.isLoading,
    error: workflowTemplatesQuery.error || workflowExecutionsQuery.error,
    createWorkflow: createWorkflowMutation.mutate,
    updateWorkflow: updateWorkflowMutation.mutate,
    deleteWorkflow: deleteWorkflowMutation.mutate,
    toggleWorkflowStatus: toggleWorkflowStatusMutation.mutate,
    executeWorkflow: executeWorkflowMutation.mutate,
    isCreating: createWorkflowMutation.isPending,
    isUpdating: updateWorkflowMutation.isPending,
    isDeleting: deleteWorkflowMutation.isPending,
    isToggling: toggleWorkflowStatusMutation.isPending,
    isExecuting: executeWorkflowMutation.isPending,
  };
}